import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';
import { SET_REGISTRY } from './setRegistry';
import { getTimelinePreview } from './timelineData';

const ResourcesPage = () => {
  const [user, setUser] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [summariesLoading, setSummariesLoading] = useState(true);
  // One row of the desktop grid (3 columns) per "page"; Load More reveals another row.
  const SUMMARIES_PER_PAGE = 3;
  const [visibleSummaries, setVisibleSummaries] = useState(SUMMARIES_PER_PAGE);
  const [yearFilter, setYearFilter] = useState('ALL');
  const [lineFilter, setLineFilter] = useState('ALL');
  const [artists, setArtists] = useState([]);
  const [artistsLoading, setArtistsLoading] = useState(true);

  // Filter options derived from the loaded rows: years keep the query's
  // newest-first order, set lines are alphabetical.
  const summaryYears = [...new Set(summaries.map(s => s.year))];
  const summaryLines = [...new Set(summaries.map(s => s.name))].sort();
  const filteredSummaries = summaries.filter(s =>
    (yearFilter === 'ALL' || s.year === yearFilter) &&
    (lineFilter === 'ALL' || s.name === lineFilter)
  );

  const pickFilter = (setter) => (value) => {
    setter(value);
    setVisibleSummaries(SUMMARIES_PER_PAGE);
  };

  const filterChip = (label, active, onClick) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--green)' : 'transparent',
        color: active ? 'var(--bg)' : 'var(--muted-3)',
        border: `1px solid ${active ? 'var(--green)' : 'var(--border-3)'}`,
        borderRadius: '3px',
        padding: '4px 10px',
        fontFamily: "'Orbitron', monospace",
        fontSize: '9px',
        fontWeight: '700',
        letterSpacing: '1px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {label}
    </button>
  );

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadSummaries() {
      const { data: rows, error } = await supabase
        .from('set_summaries')
        .select('set_id, year, brand, title, updated_at')
        .eq('status', 'published')
        .order('year', { ascending: false });
      if (cancelled) return;
      if (!error && rows) {
        // Enrich each row with totalCards / shortName from the SET_REGISTRY
        const enriched = rows.map(r => {
          const reg = SET_REGISTRY.find(s => s.id === r.set_id);
          return {
            setId: r.set_id,
            year: r.year,
            name: reg?.shortName?.replace(/\s+\d{4}$/, '') || r.title || r.set_id,
            cards: reg?.totalCards ? `${reg.totalCards} cards` : '',
            url: `/resources/sets/${r.set_id}`,
          };
        });
        setSummaries(enriched);
      }
      setSummariesLoading(false);
    }
    loadSummaries();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadArtists() {
      const { data, error } = await supabase
        .from('artists')
        .select('slug, name, city, country, artist_sets(card_count_stated, contribution_type)')
        .eq('status', 'published')
        .order('name');
      if (cancelled) return;
      if (!error && data) {
        setArtists(data.map(a => {
          // Same rule as ArtistIndex: illustrated insert sets are printed runs,
          // not sketch work, so they never count toward sets or cards drawn.
          const sketchSets = (a.artist_sets || []).filter(s => s.contribution_type !== 'illustrated');
          const cardCount = sketchSets.reduce((sum, s) => sum + (s.card_count_stated || 0), 0);
          return {
            slug: a.slug,
            name: a.name,
            location: [a.city, a.country].filter(Boolean).join(', '),
            setCount: sketchSets.length,
            cardCount: cardCount ? `~${(Math.round(cardCount / 50) * 50).toLocaleString('en-US')}` : '',
          };
        }));
      }
      setArtistsLoading(false);
    }
    loadArtists();
    return () => { cancelled = true; };
  }, []);

  const navigateTo = (path) => {
    window.location.href = path;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "'Share Tech Mono', monospace" }}>
      {user && <NavDrawer user={user} />}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 24px 80px' }}>
        
        {/* Header Section - matches DeploymentsPage pattern */}
        <div style={{ paddingBottom: '28px', borderBottom: '1px solid var(--border)', marginBottom: '40px' }}>
          <a href="/" style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--muted-2)', letterSpacing: '3px', textDecoration: 'none', display: 'inline-block', marginBottom: '16px' }}>← BACK TO APP</a>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--brand-gold)', letterSpacing: '4px', marginBottom: '8px' }}>COLLECTION KEEPER</div>
          <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '28px', fontWeight: 900, color: 'var(--text-strong)', letterSpacing: '2px', marginBottom: '6px' }}>RESOURCES</div>
          <div style={{ fontSize: '11px', color: 'var(--muted-2)', letterSpacing: '2px' }}>// EVERYTHING YOU NEED TO MASTER STAR WARS CARD COLLECTING //</div>
        </div>

        {/* Timeline Section */}
        <section style={{ marginBottom: '56px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontFamily: "'Orbitron', monospace",
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--cyan)',
              margin: 0,
              letterSpacing: '1px'
            }}>
              SET TIMELINE
            </h2>
            
            <button
              onClick={() => navigateTo('/resources/timeline')}
              style={{
                background: 'var(--cyan)',
                color: 'var(--bg)',
                border: 'none',
                padding: '8px 16px',
                fontFamily: "'Orbitron', monospace",
                fontSize: '10px',
                fontWeight: '700',
                borderRadius: '3px',
                cursor: 'pointer',
                letterSpacing: '1px'
              }}
            >
              VIEW FULL TIMELINE
            </button>
          </div>
          
          <div style={{ 
            background: 'var(--panel-deep)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '24px'
          }}>
            <p style={{ 
              fontSize: '13px', 
              color: 'var(--muted-3)', 
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              Every Topps Star Wars set from 2015 to 2026. Release dates, set sizes and key milestones.
            </p>
            
            {/* Recent Sets Preview */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
              gap: '12px'
            }}>
              {getTimelinePreview().map((set, i) => {
                const Card = set.href ? 'a' : 'div';
                const highlight = (e, on) => { e.currentTarget.style.borderColor = on ? 'var(--gold)' : 'var(--border-3)'; };
                return (
                <Card key={i} {...(set.href ? {
                  href: set.href,
                  'aria-label': `${set.year} ${set.shortName} set summary`,
                  onMouseEnter: e => highlight(e, true),
                  onMouseLeave: e => highlight(e, false),
                  onFocus: e => highlight(e, true),
                  onBlur: e => highlight(e, false),
                } : {})} style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border-3)',
                  borderRadius: '4px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textDecoration: 'none',
                  cursor: set.href ? 'pointer' : 'default',
                  transition: 'border-color 0.2s ease'
                }}>
                  <div>
                    <div style={{ 
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--gold)',
                      marginBottom: '4px',
                      letterSpacing: '1px'
                    }}>
                      {set.year}
                    </div>
                    <div style={{ 
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-strong)',
                      marginBottom: '2px'
                    }}>
                      {set.shortName}{set.href ? ' ↗' : ''}
                    </div>
                    <div style={{ 
                      fontSize: '11px',
                      color: 'var(--muted-3)'
                    }}>
                      {set.date}
                    </div>
                  </div>
                  
                  <div style={{
                    background: set.badge === 'Current' ? 'var(--green)' : set.badge === 'Upcoming' ? 'var(--gold)' : 'var(--muted-2)',
                    color: set.badge === 'Current' || set.badge === 'Upcoming' ? 'var(--bg)' : '#ffffff',
                    padding: '3px 6px',
                    borderRadius: '2px',
                    fontSize: '9px',
                    fontFamily: "'Orbitron', monospace",
                    fontWeight: '700',
                    letterSpacing: '1px'
                  }}>
                    {set.badge}
                  </div>
                </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Set Summaries Section */}
        <section style={{ marginBottom: '56px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontFamily: "'Orbitron', monospace",
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--green)',
              margin: 0,
              letterSpacing: '1px'
            }}>
              SET SUMMARIES
            </h2>
          </div>
          
          <div style={{ 
            background: 'var(--panel-deep)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '24px'
          }}>
            <p style={{ 
              fontSize: '13px', 
              color: 'var(--muted-3)', 
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              Detailed infographics breaking down set composition, parallel structures,
              pull rates, and collecting strategies for major releases.
            </p>

            {/* Filters */}
            {!summariesLoading && summaries.length > 0 && (
              <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--muted-2)', letterSpacing: '2px', minWidth: '38px' }}>YEAR</span>
                  {filterChip('ALL', yearFilter === 'ALL', () => pickFilter(setYearFilter)('ALL'))}
                  {summaryYears.map(y => filterChip(y, yearFilter === y, () => pickFilter(setYearFilter)(y)))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: '9px', color: 'var(--muted-2)', letterSpacing: '2px', minWidth: '38px' }}>SET</span>
                  {filterChip('ALL', lineFilter === 'ALL', () => pickFilter(setLineFilter)('ALL'))}
                  {summaryLines.map(l => filterChip(l.toUpperCase(), lineFilter === l, () => pickFilter(setLineFilter)(l)))}
                </div>
              </div>
            )}

            {/* Available Summaries */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '16px'
            }}>
              {summariesLoading ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0', fontFamily: "'Orbitron', monospace", fontSize: '11px', color: 'var(--cyan)', letterSpacing: '4px' }}>// LOADING SET SUMMARIES</div>
              ) : summaries.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0', fontSize: '13px', color: 'var(--muted-2)' }}>No published set summaries yet.</div>
              ) : filteredSummaries.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0', fontSize: '13px', color: 'var(--muted-2)' }}>No summaries match those filters.</div>
              ) : filteredSummaries.slice(0, visibleSummaries).map((summary, i) => (
                <div key={i} 
                  onClick={() => navigateTo(summary.url)}
                  style={{
                    background: 'var(--bg)',
                    border: '1px solid var(--border-3)',
                    borderRadius: '4px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--green)';
                    e.currentTarget.style.background = 'rgba(var(--green-rgb),0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-3)';
                    e.currentTarget.style.background = 'var(--bg)';
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--gold)',
                      marginBottom: '4px',
                      letterSpacing: '1px'
                    }}>
                      {summary.year}
                    </div>
                    <div style={{ 
                      fontSize: '16px',
                      fontWeight: '700',
                      color: 'var(--text-strong)',
                      marginBottom: '4px'
                    }}>
                      {summary.name}
                    </div>
                    <div style={{ 
                      fontSize: '12px',
                      color: 'var(--green)',
                      fontWeight: '600'
                    }}>
                      {summary.cards}
                    </div>
                  </div>
                  
                  <div style={{
                    background: 'var(--green)',
                    color: 'var(--bg)',
                    padding: '3px 6px',
                    borderRadius: '2px',
                    fontSize: '9px',
                    fontFamily: "'Orbitron', monospace",
                    fontWeight: '700',
                    display: 'inline-block',
                    letterSpacing: '1px'
                  }}>
                    PUBLISHED
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {!summariesLoading && filteredSummaries.length > visibleSummaries && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setVisibleSummaries(c => c + SUMMARIES_PER_PAGE)}
                  style={{
                    background: 'transparent',
                    color: 'var(--green)',
                    border: '1px solid var(--green)',
                    padding: '10px 24px',
                    fontFamily: "'Orbitron', monospace",
                    fontSize: '10px',
                    fontWeight: '700',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    letterSpacing: '2px',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--green)'; e.currentTarget.style.color = 'var(--bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--green)'; }}
                >
                  LOAD MORE ({filteredSummaries.length - visibleSummaries})
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Sketch Artists Section */}
        <section style={{ marginBottom: '56px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--gold)',
              margin: 0,
              letterSpacing: '1px'
            }}>
              SKETCH ARTISTS
            </h2>

            <button
              onClick={() => navigateTo('/resources/artists')}
              style={{
                background: 'var(--gold)',
                color: 'var(--bg)',
                border: 'none',
                padding: '8px 16px',
                fontFamily: "'Orbitron', monospace",
                fontSize: '10px',
                fontWeight: '700',
                borderRadius: '3px',
                cursor: 'pointer',
                letterSpacing: '1px'
              }}
            >
              VIEW ARTIST PROFILES
            </button>
          </div>

          <div style={{
            background: 'var(--panel-deep)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '24px'
          }}>
            <p style={{
              fontSize: '13px',
              color: 'var(--muted-3)',
              margin: '0 0 20px 0',
              lineHeight: '1.6'
            }}>
              Meet the people who draw the one-of-a-kind sketch cards hiding in Star Wars packs.
              Every profile shows where each fact came from — and says so plainly when something
              is still unconfirmed.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px'
            }}>
              {artistsLoading ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0', fontFamily: "'Orbitron', monospace", fontSize: '11px', color: 'var(--cyan)', letterSpacing: '4px' }}>// LOADING ARTIST PROFILES</div>
              ) : artists.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 0', fontSize: '13px', color: 'var(--muted-2)' }}>No published artist profiles yet.</div>
              ) : artists.map(artist => (
                <div key={artist.slug}
                  onClick={() => navigateTo(`/resources/artists/${artist.slug}`)}
                  style={{
                    background: 'var(--bg)',
                    border: '1px solid var(--border-3)',
                    borderRadius: '4px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'rgba(var(--gold-rgb),0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-3)';
                    e.currentTarget.style.background = 'var(--bg)';
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--text-strong)',
                      marginBottom: '4px',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>
                      {artist.location || 'Sketch artist'}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: 'var(--gold)',
                      marginBottom: '4px'
                    }}>
                      {artist.name}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--green)',
                      fontWeight: '600'
                    }}>
                      {artist.setCount} sketch {artist.setCount === 1 ? 'set' : 'sets'}{artist.cardCount ? ` · ${artist.cardCount} cards` : ''}
                    </div>
                  </div>

                  <div style={{
                    background: 'var(--green)',
                    color: 'var(--bg)',
                    padding: '3px 6px',
                    borderRadius: '2px',
                    fontSize: '9px',
                    fontFamily: "'Orbitron', monospace",
                    fontWeight: '700',
                    display: 'inline-block',
                    letterSpacing: '1px'
                  }}>
                    PUBLISHED
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How-to Videos Section */}
        <section style={{ marginBottom: '56px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontFamily: "'Orbitron', monospace",
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--purple)',
              margin: 0,
              letterSpacing: '1px'
            }}>
              HOW-TO VIDEOS
            </h2>
            
            <div style={{
              background: 'rgba(var(--purple-rgb),0.1)',
              color: 'var(--purple)',
              border: '1px solid var(--purple)',
              padding: '6px 12px',
              fontFamily: "'Orbitron', monospace",
              fontSize: '9px',
              fontWeight: '700',
              borderRadius: '3px',
              letterSpacing: '1px'
            }}>
              COMING SOON
            </div>
          </div>
          
          <div style={{ 
            background: 'var(--panel-deep)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '24px'
          }}>
            <p style={{ 
              fontSize: '13px', 
              color: 'var(--muted-3)', 
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              Video tutorials covering collecting strategies, grading tips, storage solutions, 
              and how to get the most out of CollectionKeeper.
            </p>
            
            {/* Placeholder Video Topics */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '12px'
            }}>
              {[
                'Getting Started with CollectionKeeper',
                'Understanding Parallel Cards',  
                'Grading and Authentication',
                'Building Your First Set'
              ].map((topic, i) => (
                <div key={i} style={{
                  background: 'var(--bg)',
                  border: '1px solid var(--border-3)',
                  borderRadius: '4px',
                  padding: '16px',
                  opacity: 0.6
                }}>
                  <div style={{ 
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-strong)',
                    marginBottom: '6px'
                  }}>
                    {topic}
                  </div>
                  <div style={{ 
                    fontSize: '11px',
                    color: 'var(--muted-3)'
                  }}>
                    Coming Soon
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Future Resources Section */}
        <section>
          <h2 style={{ 
            fontFamily: "'Orbitron', monospace",
            fontSize: '20px',
            fontWeight: '700',
            color: 'var(--muted-3)',
            margin: '0 0 24px 0',
            letterSpacing: '1px'
          }}>
            MORE RESOURCES
          </h2>
          
          <div style={{ 
            background: 'var(--panel-deep)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <p style={{ 
              fontSize: '14px', 
              color: 'var(--muted-3)', 
              margin: 0,
              lineHeight: '1.6'
            }}>
              🚀 This section will grow with collecting guides, market insights, 
              community spotlights, and more collector-focused content.
            </p>
            
            <div style={{ 
              marginTop: '16px',
              fontSize: '11px',
              color: 'var(--muted-2)',
              fontStyle: 'italic'
            }}>
              Have an idea for a resource? Let us know!
            </div>
          </div>
        </section>
        
      </div>
    </div>
  );
};

export default ResourcesPage;